import { createServerClient as createSupabaseServerClient, createBrowserClient as createSupabaseBrowserClient } from '@supabase/ssr';
import { Database } from './database.types';
import { createLogger } from './utils/logger';
import type { SupabaseClient as SupabaseClientType } from '@supabase/supabase-js';
import { getSupabaseConfig as getSupabaseEnvConfig } from './utils/env-config';
import type { NextRequest, NextResponse } from 'next/server';

const logger = createLogger('supabase:clients');

// Common configuration - use centralized env config
function getSupabaseConfig() {
  const envConfig = getSupabaseEnvConfig();
  
  if (!envConfig.url || !envConfig.anonKey) {
    console.warn('[Supabase] Missing environment variables - using defaults');
  }
  
  return {
    url: envConfig.url || 'https://lpjvndsvkvqgkdkvacnn.supabase.co',
    anonKey: envConfig.anonKey || '',
    serviceKey: envConfig.serviceKey
  };
}

// Extract project reference for cookie naming
function getProjectRef(): string {
  // Use the URL from environment or config depending on context
  const url = typeof window !== 'undefined' 
    ? process.env.NEXT_PUBLIC_SUPABASE_URL 
    : getSupabaseConfig().url;
  const match = url?.match(/https:\/\/([^.]+)\.supabase\.co/);
  return match?.[1] || 'lpjvndsvkvqgkdkvacnn';
}

// Cookie name getter
function getCookieName(): string {
  return `sb-${getProjectRef()}-auth-token`;
}

// Export specific type for our database
export type SupabaseClient = SupabaseClientType<Database>;

// Singleton instance for browser client to prevent multiple instances
let browserClient: SupabaseClient | null = null;

/**
 * Creates a Supabase client for server components
 *
 * Use this client in server components to access Supabase services
 * with the user's authentication context.
 *
 * IMPORTANT: This is an async function in Next.js 15 - must be awaited!
 */
export async function createServerClient() {
  try {
    // Dynamic import to avoid issues in client components
    const { cookies } = require('next/headers');
    // Get cookie store directly from Next.js - cookies() returns Promise in Next.js 15
    const cookieJar = await cookies();
    const config = getSupabaseConfig();
    
    // Create server client with cookie manager for consistent cookie handling
    return createSupabaseServerClient<Database>(
      config.url,
      config.anonKey,
      {
        cookies: {
          get(name) {
            const cookie = cookieJar.get(name);
            return cookie?.value || null;
          },
          set(name, value, options) {
            // Server components can't set cookies - this is expected behavior
            // Supabase may try to refresh tokens, but this will be handled by middleware
          },
          remove(name, options) {
            // Server components can't remove cookies - this is expected behavior
          }
        }
      }
    ) as SupabaseClient;
  } catch (error) {
    logger.error('Failed to create server client', error);
    // Return a fallback client that will work but with no auth
    const config = getSupabaseConfig();
    return createSupabaseServerClient<Database>(
      config.url,
      config.anonKey,
      {
        cookies: {
          get: () => null,
          set: () => {},
          remove: () => {}
        }
      }
    ) as SupabaseClient;
  }
}

/**
 * Creates a Supabase client for client components
 * 
 * Use this client in client components to access Supabase
 * services with the user's authentication context.
 * Uses singleton pattern to prevent multiple client instances.
 */
export function createBrowserClient() {
  if (!browserClient) {
    // In the browser, we need to use the environment variables directly
    // as they are replaced at build time by Next.js
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    
    if (!url || !anonKey) {
      throw new Error(
        '@supabase/ssr: Your project\'s URL and API key are required to create a Supabase client!\n\n' +
        'Check your Supabase project\'s API settings to find these values\n\n' +
        'https://supabase.com/dashboard/project/_/settings/api'
      );
    }
    
    // For browser clients, @supabase/ssr handles cookies automatically
    // No need to configure cookie options - it uses document.cookie internally
    browserClient = createSupabaseBrowserClient<Database>(
      url,
      anonKey
    ) as SupabaseClient;
  }
  return browserClient;
}

/**
 * Creates a Supabase client for Route Handlers (API routes)
 *
 * Use this client in API route handlers to access Supabase
 * services with the user's authentication context.
 * This client can properly set and remove cookies in route handlers.
 *
 * IMPORTANT: This is an async function in Next.js 15 - must be awaited!
 */
export async function createRouteHandlerClient() {
  // Dynamic import to avoid issues in client components
  const { cookies } = require('next/headers');
  // cookies() returns Promise in Next.js 15
  const cookieJar = await cookies();
  const config = getSupabaseConfig();

  return createSupabaseServerClient<Database>(
    config.url,
    config.anonKey,
    {
      cookies: {
        get(name) {
          const cookie = cookieJar.get(name);
          return cookie?.value || null;
        },
        set(name, value, options) {
          try {
            cookieJar.set({ name, value, ...options });
          } catch (error) {
            logger.warn(`[RouteHandler] Cannot set cookie ${name}:`, error);
          }
        },
        remove(name, options) {
          try {
            cookieJar.set({ name, value: '', ...options });
          } catch (error) {
            logger.warn(`[RouteHandler] Cannot remove cookie ${name}:`, error);
          }
        }
      }
    }
  ) as SupabaseClient;
}

/**
 * Creates a Supabase client for Next.js Middleware
 * 
 * Use this client in middleware.ts to handle authentication
 * and session management. This client properly handles cookies
 * in the middleware context.
 */
export function createMiddlewareClient(
  request: NextRequest,
  response: NextResponse
) {
  const config = getSupabaseConfig();
  
  return createSupabaseServerClient<Database>(
    config.url,
    config.anonKey,
    {
      cookies: {
        get(name) {
          return request.cookies.get(name)?.value || null;
        },
        set(name, value, options) {
          // Set cookie on response
          response.cookies.set({
            name,
            value,
            ...options,
          });
        },
        remove(name, options) {
          // Remove cookie on response
          response.cookies.set({
            name,
            value: '',
            ...options,
            maxAge: 0,
          });
        }
      }
    }
  ) as SupabaseClient;
}

